<script setup>
import { ref, watchEffect, nextTick, onMounted, onBeforeUnmount } from 'vue';

const props = defineProps({
  open: {
    type: Boolean,
    default: false
  },
  pedido: {
    type: Object,
    required: true
  },
  loading: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['update:open', 'confirm', 'cancel']);

// Initialize with saved value from sessionStorage if available
const savedDeliveryTime = sessionStorage.getItem('lastDeliveryTime') || '30';
const deliveryTime = ref(savedDeliveryTime);
const inputRef = ref(null);

// Close modal handler - don't reset to default
const closeModal = () => {
  emit('update:open', false);
  // Don't reset the value when closing
};

// Confirm delivery time
const confirmDeliveryTime = () => {
  // Convert to number and validate
  const minutes = parseInt(deliveryTime.value);
  if (isNaN(minutes) || minutes <= 0) {
    return; // Invalid input
  }
  
  emit('confirm', minutes);
  // Modal will be closed by parent component after successful operation
};

// Focus the input when modal opens
watchEffect(() => {
  if (props.open && !props.loading) {
    nextTick(() => {
      if (inputRef.value) {
        inputRef.value.focus();
      }
    });
  }
});

// Save delivery time to sessionStorage when it changes
watchEffect(() => {
  if (deliveryTime.value) {
    sessionStorage.setItem('lastDeliveryTime', deliveryTime.value);
  }
});

// Handle form submission
const handleSubmit = (e) => {
  e.preventDefault();
  confirmDeliveryTime();
};

// Register this modal in global state when open
onMounted(() => {
  // Pre-calculate commonly used values
  const pedidoId = props.pedido?.id || '';
  
  // Update the global open modals tracker
  watchEffect(() => {
    if (props.open) {
      window.pedidoModalOpenCount = (window.pedidoModalOpenCount || 0) + 1;
    } else {
      window.pedidoModalOpenCount = Math.max(0, (window.pedidoModalOpenCount || 0) - 1);
    }
  });
});

onBeforeUnmount(() => {
  // Ensure we decrease the counter when component is unmounted while open
  if (props.open) {
    window.pedidoModalOpenCount = Math.max(0, (window.pedidoModalOpenCount || 0) - 1);
  }
});
</script>

<template>
  <!-- Use teleport to move the modal outside the current component tree -->
  <teleport to="body">
    <div v-if="open" class="modal-backdrop" @click="closeModal"></div>
    
    <div 
      class="modal-container" 
      :class="{ 'modal-open': open }"
      v-if="open"
    >
      <div class="modal-content">
        <div class="modal-header">
          <h2 class="modal-title">
            <v-icon start icon="mdi-truck-delivery" class="mr-2"></v-icon>
            Saindo para entrega - Pedido #{{ pedido?.id }}
          </h2>
          <button class="modal-close" @click="closeModal">×</button>
        </div>
        
        <form @submit.prevent="handleSubmit">
          <div class="modal-body">
            <p class="modal-text">
              Quanto tempo você estima que o pedido levará para chegar ao cliente?
            </p>
            
            <v-text-field
              ref="inputRef"
              v-model="deliveryTime"
              label="Tempo estimado (minutos)"
              type="number"
              min="1"
              max="120"
              variant="outlined"
              :disabled="loading"
              hint="Informe entre 1 e 120 minutos"
            ></v-text-field>
          </div>
          
          <div class="modal-footer">
            <button
              type="button"
              class="modal-btn cancel-btn"
              @click="closeModal"
              :disabled="loading"
            >
              Cancelar
            </button>
            
            <button
              type="submit"
              class="modal-btn confirm-btn"
              :disabled="loading || !deliveryTime || parseInt(deliveryTime) <= 0"
            >
              <span v-if="loading" class="loader"></span>
              <span v-else>Confirmar</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </teleport>
</template>

<style scoped>
/* Custom modal styling for better performance */
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: transparent;
  z-index: 1000;
}

.modal-container {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
  pointer-events: none;
}

.modal-content {
  width: 100%;
  max-width: 500px;
  background-color: white;
  border-radius: 4px;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.15);
  pointer-events: auto;
  margin: 0 16px;
  transform: translateY(0);
  transition: transform 0.2s ease-out;
  will-change: transform;
}

.modal-open .modal-content {
  transform: translateY(0);
}

.modal-header {
  background-color: #2196F3;
  color: white;
  padding: 16px 20px;
  border-radius: 4px 4px 0 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-title {
  font-size: 1.25rem;
  font-weight: 500;
  margin: 0;
  display: flex;
  align-items: center;
}

.modal-close {
  background: transparent;
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 16px;
  transition: background-color 0.2s;
}

.modal-close:hover {
  background-color: rgba(255, 255, 255, 0.2);
}

.modal-body {
  padding: 20px 16px 0;
}

.modal-text {
  margin-top: 0;
  margin-bottom: 16px;
  font-size: 1rem;
}

.modal-footer {
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-btn {
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  border: none;
  min-width: 64px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.cancel-btn {
  background-color: transparent;
  color: #757575;
}

.cancel-btn:hover:not(:disabled) {
  background-color: rgba(0, 0, 0, 0.05);
}

.confirm-btn {
  background-color: #2196F3;
  color: white;
}

.confirm-btn:hover:not(:disabled) {
  background-color: #1976D2;
}

/* Loading spinner */
.loader {
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  display: inline-block;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 500px) {
  .modal-content {
    max-width: calc(100% - 32px);
  }
  
  .modal-footer {
    flex-direction: column-reverse;
    gap: 16px;
  }
  
  .modal-actions {
    width: 100%;
    justify-content: flex-end;
  }
  
  .cancel-btn {
    width: 100%;
  }
}
</style>
